import {
  Activity,
  Bot,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  CircleDot,
  ClipboardCheck,
  Clock3,
  Copy,
  DatabaseZap,
  ExternalLink,
  Filter,
  Gauge,
  History,
  Loader2,
  RefreshCw,
  Search,
  Server,
  Sparkles,
  TriangleAlert,
  XCircle
} from 'lucide-react';
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type {
  PortalActivityHistoryResponse,
  PortalActivityKind,
  PortalActivityRecord,
  PortalActivityResponse,
  PortalActivitySummary,
  PortalActivityTraceResponse,
  PortalTraceSpan,
  SessionStatus
} from '../shared/portalTypes';

type ActivityKindFilter = 'all' | PortalActivityKind;
type ActivityOutcomeFilter = 'all' | PortalActivityRecord['outcome'];
type ActivityRangePreset = 'live' | '1h' | '24h' | '7d' | '30d' | 'custom';

interface AppliedActivityRange {
  preset: ActivityRangePreset;
  from?: string;
  to?: string;
}

const RefreshIntervalMilliseconds = 30_000;
const MaximumHistoryRangeMilliseconds = 30 * 24 * 60 * 60 * 1000;
const RangePresets: Array<{ value: Exclude<ActivityRangePreset, 'custom'>; label: string; milliseconds?: number }> = [
  { value: 'live', label: 'Live' },
  { value: '1h', label: '1 hour', milliseconds: 60 * 60 * 1000 },
  { value: '24h', label: '24 hours', milliseconds: 24 * 60 * 60 * 1000 },
  { value: '7d', label: '7 days', milliseconds: 7 * 24 * 60 * 60 * 1000 },
  { value: '30d', label: '30 days', milliseconds: MaximumHistoryRangeMilliseconds }
];
const KindFilters: Array<{ value: ActivityKindFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'rest', label: 'REST' },
  { value: 'mcp', label: 'MCP' },
  { value: 'ai', label: 'AI' },
  { value: 'job', label: 'Jobs' }
];

export function ActivityView({ active, sessionStatus, onAskAi }: {
  active: boolean;
  sessionStatus: SessionStatus;
  onAskAi(message: string): void;
}): ReactNode {
  const [liveActivities, setLiveActivities] = useState<PortalActivityRecord[]>([]);
  const [history, setHistory] = useState<PortalActivityHistoryResponse>();
  const [appliedRange, setAppliedRange] = useState<AppliedActivityRange>({ preset: 'live' });
  const [customRangeOpen, setCustomRangeOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState(() => formatDateTimeLocal(Date.now() - 24 * 60 * 60 * 1000));
  const [customTo, setCustomTo] = useState(() => formatDateTimeLocal(Date.now()));
  const [customRangeError, setCustomRangeError] = useState('');
  const [selectedActivityId, setSelectedActivityId] = useState('');
  const [kindFilter, setKindFilter] = useState<ActivityKindFilter>('all');
  const [outcomeFilter, setOutcomeFilter] = useState<ActivityOutcomeFilter>('all');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastLoadedAt, setLastLoadedAt] = useState<number>();
  const [trace, setTrace] = useState<PortalActivityTraceResponse>();
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceError, setTraceError] = useState('');
  const [supportBundleCopied, setSupportBundleCopied] = useState(false);
  const loadSequence = useRef(0);
  const traceLoadSequence = useRef(0);
  const deferredQuery = useDeferredValue(query.trim().toLowerCase());
  const activities = appliedRange.preset === 'live' ? liveActivities : history?.activities ?? [];

  const loadActivities = useCallback(async () => {
    const sequence = ++loadSequence.current;
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/portal/session/activity', { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(response.status === 401 ? 'Reconnect to view integration activity.' : `Activity returned ${response.status}.`);
      }
      const payload = await response.json() as PortalActivityResponse;
      if (sequence !== loadSequence.current) return;
      setLiveActivities(payload.activities);
      setSelectedActivityId((current) => (
        current && payload.activities.some((activity) => activity.id === current)
          ? current
          : payload.activities[0]?.id ?? ''
      ));
      setLastLoadedAt(Date.now());
    } catch (loadError) {
      if (sequence !== loadSequence.current) return;
      setError(loadError instanceof Error ? loadError.message : 'Unable to load integration activity.');
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async (from: Date, to: Date, preset: ActivityRangePreset) => {
    const sequence = ++loadSequence.current;
    setLoading(true);
    setError('');
    try {
      const search = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
      const response = await fetch(`/portal/session/activity/history?${search}`, { cache: 'no-store' });
      const payload = await response.json().catch(() => undefined) as PortalActivityHistoryResponse & { error?: string } | undefined;
      if (!response.ok || !payload) {
        throw new Error(payload?.error ?? `Activity history returned ${response.status}.`);
      }
      if (sequence !== loadSequence.current) return;
      setHistory(payload);
      setAppliedRange({ preset, from: payload.from, to: payload.to });
      setSelectedActivityId(payload.activities[0]?.id ?? '');
      ++traceLoadSequence.current;
      setTrace(undefined);
      setTraceError('');
      setLastLoadedAt(Date.now());
      setCustomRangeOpen(false);
    } catch (loadError) {
      if (sequence !== loadSequence.current) return;
      setError(loadError instanceof Error ? loadError.message : 'Unable to load activity history.');
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!active || appliedRange.preset !== 'live') return;
    void loadActivities();
    const refreshTimer = window.setInterval(() => void loadActivities(), RefreshIntervalMilliseconds);
    return () => window.clearInterval(refreshTimer);
  }, [active, appliedRange.preset, loadActivities]);

  const selectRangePreset = (preset: Exclude<ActivityRangePreset, 'custom'>) => {
    setCustomRangeError('');
    setCustomRangeOpen(false);
    if (preset === 'live') {
      ++loadSequence.current;
      setLoading(false);
      setError('');
      setAppliedRange({ preset: 'live' });
      setSelectedActivityId(liveActivities[0]?.id ?? '');
      setTrace(undefined);
      setTraceError('');
      return;
    }
    const duration = RangePresets.find((candidate) => candidate.value === preset)?.milliseconds;
    if (!duration) return;
    const to = new Date();
    void loadHistory(new Date(to.getTime() - duration), to, preset);
  };

  const applyCustomRange = () => {
    const from = new Date(customFrom);
    const to = new Date(customTo);
    if (!Number.isFinite(from.getTime()) || !Number.isFinite(to.getTime()) || from >= to) {
      setCustomRangeError('Choose a start time before the end time.');
      return;
    }
    if (to.getTime() - from.getTime() > MaximumHistoryRangeMilliseconds) {
      setCustomRangeError('Choose a range of 30 days or less.');
      return;
    }
    if (from.getTime() < Date.now() - MaximumHistoryRangeMilliseconds - 5 * 60 * 1000) {
      setCustomRangeError('Choose a start time within the last 30 days.');
      return;
    }
    if (to.getTime() > Date.now() + 5 * 60 * 1000) {
      setCustomRangeError('The end time cannot be in the future.');
      return;
    }
    setCustomRangeError('');
    void loadHistory(from, to, 'custom');
  };

  const refreshActivity = () => {
    if (appliedRange.preset === 'live') {
      void loadActivities();
      return;
    }
    if (appliedRange.from && appliedRange.to) {
      void loadHistory(new Date(appliedRange.from), new Date(appliedRange.to), appliedRange.preset);
    }
  };

  const filteredActivities = useMemo(() => activities.filter((activity) => {
    if (kindFilter !== 'all' && activity.kind !== kindFilter) return false;
    if (outcomeFilter !== 'all' && activity.outcome !== outcomeFilter) return false;
    if (!deferredQuery) return true;
    return [activity.title, activity.detail, activity.method, activity.apiId, activity.toolName, activity.correlationId]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(deferredQuery);
  }), [activities, deferredQuery, kindFilter, outcomeFilter]);

  useEffect(() => {
    if (filteredActivities.length > 0 && !filteredActivities.some((activity) => activity.id === selectedActivityId)) {
      setSelectedActivityId(filteredActivities[0].id);
    }
  }, [filteredActivities, selectedActivityId]);

  const selectedActivity = activities.find((activity) => activity.id === selectedActivityId);
  const liveSummary = summarizeActivities(activities);
  const activitySummary = appliedRange.preset === 'live' ? liveSummary : history?.summary ?? liveSummary;
  const successRate = activitySummary.total === 0 ? 0 : Math.round((activitySummary.succeeded / activitySummary.total) * 100);
  const rangeLabel = formatAppliedRange(appliedRange);

  const selectActivity = (activityId: string) => {
    setSelectedActivityId(activityId);
    ++traceLoadSequence.current;
    setTrace(undefined);
    setTraceError('');
    setSupportBundleCopied(false);
  };

  const loadTrace = useCallback(async (activity: PortalActivityRecord) => {
    const sequence = ++traceLoadSequence.current;
    setTraceLoading(true);
    setTraceError('');
    try {
      const historical = activity.id.startsWith('history:') && activity.traceId && activity.rootSpanId;
      const tracePath = historical
        ? `/portal/session/activity/history/${encodeURIComponent(activity.traceId!)}/trace?${new URLSearchParams({ startedAt: activity.startedAt, rootSpanId: activity.rootSpanId! })}`
        : `/portal/session/activity/${encodeURIComponent(activity.id)}/trace`;
      const response = await fetch(tracePath, { cache: 'no-store' });
      const payload = await response.json().catch(() => undefined) as PortalActivityTraceResponse & { error?: string } | undefined;
      if (!response.ok) {
        throw new Error(payload?.error ?? `Trace lookup returned ${response.status}.`);
      }
      if (sequence !== traceLoadSequence.current) return;
      setTrace(payload);
    } catch (loadError) {
      if (sequence !== traceLoadSequence.current) return;
      setTrace(undefined);
      setTraceError(loadError instanceof Error ? loadError.message : 'Unable to load the trace timeline.');
    } finally {
      if (sequence === traceLoadSequence.current) setTraceLoading(false);
    }
  }, []);

  const copySupportBundle = useCallback(async (activity: PortalActivityRecord) => {
    await navigator.clipboard.writeText(createSupportBundle(activity, sessionStatus));
    setSupportBundleCopied(true);
    window.setTimeout(() => setSupportBundleCopied(false), 1400);
  }, [sessionStatus]);

  const askAiToDiagnose = (activity: PortalActivityRecord) => {
    onAskAi(createDiagnosisPrompt(activity, sessionStatus));
  };

  return (
    <section className="portal-view activity-view" aria-label="Integration activity">
      <header className="activity-command-header">
        <div className="activity-command-title">
          <span className="activity-command-mark"><Activity aria-hidden="true" /></span>
          <div>
            <p className="eyebrow">Operational evidence</p>
            <h2>Integration activity</h2>
            <p>Replay work, inspect distributed traces, and package the exact evidence support needs.</p>
          </div>
        </div>
        <div className="activity-live-state">
          <span>{appliedRange.preset === 'live' ? <CircleDot aria-hidden="true" /> : <Clock3 aria-hidden="true" />}{appliedRange.preset === 'live' ? 'Live session' : 'Application Insights'}</span>
          <small>{lastLoadedAt ? `${rangeLabel} · Updated ${formatRelativeTimestamp(lastLoadedAt)}` : rangeLabel}</small>
          <button className="icon-button" type="button" title="Refresh activity" aria-label="Refresh activity" disabled={loading} onClick={refreshActivity}>
            <RefreshCw className={loading ? 'spinning' : undefined} aria-hidden="true" />
          </button>
        </div>
      </header>

      <section className="activity-range" aria-label="Activity date range">
        <div className="activity-range-heading">
          <CalendarClock aria-hidden="true" />
          <span><strong>{rangeLabel}</strong><small>{appliedRange.preset === 'live' ? 'Latest 50 operations in this connected session' : 'Customer activity from Application Insights'}</small></span>
        </div>
        <div className="activity-range-presets" role="group" aria-label="Date range presets">
          {RangePresets.map((preset) => (
            <button key={preset.value} type="button" className={!customRangeOpen && appliedRange.preset === preset.value ? 'selected' : undefined} aria-pressed={!customRangeOpen && appliedRange.preset === preset.value} disabled={loading} onClick={() => selectRangePreset(preset.value)}>
              {preset.value === 'live' && <i aria-hidden="true" />}{preset.label}
            </button>
          ))}
          <button type="button" className={customRangeOpen || appliedRange.preset === 'custom' ? 'selected' : undefined} aria-pressed={customRangeOpen || appliedRange.preset === 'custom'} onClick={() => setCustomRangeOpen((open) => !open)}>Custom</button>
        </div>
        {customRangeOpen && (
          <div className="activity-custom-range">
            <label><span>From</span><input type="datetime-local" aria-invalid={Boolean(customRangeError)} aria-describedby="activity-custom-range-feedback" value={customFrom} onChange={(event) => setCustomFrom(event.currentTarget.value)} /></label>
            <span className="activity-range-arrow" aria-hidden="true">→</span>
            <label><span>To</span><input type="datetime-local" aria-invalid={Boolean(customRangeError)} aria-describedby="activity-custom-range-feedback" value={customTo} onChange={(event) => setCustomTo(event.currentTarget.value)} /></label>
            <button type="button" disabled={loading} onClick={applyCustomRange}>{loading ? <Loader2 className="spinning" aria-hidden="true" /> : <CalendarClock aria-hidden="true" />}Apply</button>
            <small id="activity-custom-range-feedback" role={customRangeError ? 'alert' : undefined} className={customRangeError ? 'error' : undefined}>{customRangeError || `Times shown in ${Intl.DateTimeFormat().resolvedOptions().timeZone}`}</small>
          </div>
        )}
      </section>

      <section className="activity-signal-strip" aria-label="Activity signals">
        <ActivitySignal value={String(activitySummary.total)} label={appliedRange.preset === 'live' ? 'Session operations' : 'Operations in range'} tone="blue" />
        <ActivitySignal value={`${successRate}%`} label="Successful" tone="mint" />
        <ActivitySignal value={formatDuration(activitySummary.medianDurationMs)} label="Median duration" tone="blue" />
        <ActivitySignal value={formatDuration(activitySummary.p95DurationMs)} label="P95 duration" tone="blue" />
        <ActivitySignal value={String(activitySummary.failed)} label="Needs attention" tone={activitySummary.failed > 0 ? 'danger' : 'muted'} />
      </section>

      <section className="activity-toolbar" aria-label="Activity filters">
        <label className="activity-search" htmlFor="activity-search">
          <Search aria-hidden="true" />
          <input id="activity-search" type="search" placeholder="Search operation, API, tool, or correlation ID" value={query} onChange={(event) => setQuery(event.currentTarget.value)} />
        </label>
        <div className="activity-segmented" role="group" aria-label="Operation type">
          {KindFilters.map((filter) => (
            <button key={filter.value} type="button" className={kindFilter === filter.value ? 'selected' : undefined} aria-pressed={kindFilter === filter.value} onClick={() => setKindFilter(filter.value)}>
              {filter.label}
            </button>
          ))}
        </div>
        <label className="activity-outcome-filter">
          <Filter aria-hidden="true" />
          <span className="sr-only">Outcome</span>
          <select value={outcomeFilter} onChange={(event) => setOutcomeFilter(event.currentTarget.value as ActivityOutcomeFilter)}>
            <option value="all">Every outcome</option>
            <option value="succeeded">Succeeded</option>
            <option value="failed">Failed</option>
          </select>
        </label>
      </section>

      {error && (
        <div className="activity-alert" role="alert">
          <TriangleAlert aria-hidden="true" />
          <span><strong>Activity unavailable</strong><small>{error}</small></span>
          <button type="button" onClick={refreshActivity}>Retry</button>
        </div>
      )}

      <div className="activity-workspace">
        <aside className="activity-ledger" aria-label="Recent operations">
          <div className="activity-ledger-heading">
            <span><History aria-hidden="true" /><strong>{appliedRange.preset === 'live' ? 'Recent operations' : 'Operations'}</strong></span>
            <small>{history?.truncated && appliedRange.preset !== 'live' ? `${filteredActivities.length} shown · newest 200` : `${filteredActivities.length} shown`}</small>
          </div>
          <div className="activity-list">
            {!loading && filteredActivities.length === 0 && (
              <div className="activity-empty-list">
                <Activity aria-hidden="true" />
                <strong>{activities.length === 0 ? (appliedRange.preset === 'live' ? 'No operations yet' : 'No activity in this range') : 'No matching activity'}</strong>
                <span>{activities.length === 0 ? (appliedRange.preset === 'live' ? 'Run an API request, agent tool, AI analysis, or job check to create evidence.' : 'Choose a wider range or return to Live activity.') : 'Adjust the type, outcome, or search filters.'}</span>
              </div>
            )}
            {filteredActivities.map((activity) => (
              <button
                key={activity.id}
                className={`activity-row ${selectedActivityId === activity.id ? 'selected' : ''}`}
                type="button"
                aria-pressed={selectedActivityId === activity.id}
                onClick={() => selectActivity(activity.id)}
              >
                <span className={`activity-kind-icon ${activity.kind}`}><ActivityKindIcon kind={activity.kind} /></span>
                <span className="activity-row-copy">
                  <span><strong>{formatActivityTitle(activity)}</strong><em>{formatRelativeTimestamp(Date.parse(activity.startedAt))}</em></span>
                  <small>{activity.detail}</small>
                  <span className="activity-row-meta">
                    <ActivityOutcome activity={activity} />
                    <span>{formatDuration(activity.durationMs)}</span>
                    <code>{activity.kind.toUpperCase()}</code>
                  </span>
                </span>
                <ChevronRight aria-hidden="true" />
              </button>
            ))}
          </div>
        </aside>

        <section className="activity-evidence" aria-label="Selected operation evidence">
          {!selectedActivity && (
            <div className="activity-empty-evidence">
              <Gauge aria-hidden="true" />
              <h3>Select an operation</h3>
              <p>Its identifiers, replay controls, support bundle, and distributed trace will appear here.</p>
            </div>
          )}

          {selectedActivity && (
            <>
              <header className="activity-evidence-header">
                <div>
                  <span className={`activity-kind-label ${selectedActivity.kind}`}><ActivityKindIcon kind={selectedActivity.kind} />{activityKindLabel(selectedActivity.kind)}</span>
                  <h3>{formatActivityTitle(selectedActivity)}</h3>
                  <p>{selectedActivity.detail}</p>
                </div>
                <ActivityOutcome activity={selectedActivity} prominent />
              </header>

              <div className="activity-action-bar" aria-label="Activity actions">
                <a className="activity-action primary" href={createReplayHref(selectedActivity)}>
                  <ExternalLink aria-hidden="true" /><span>{replayLabel(selectedActivity)}</span>
                </a>
                {sessionStatus.isSuperUser && <button className="activity-action" type="button" onClick={() => askAiToDiagnose(selectedActivity)}>
                  <Sparkles aria-hidden="true" /><span>Ask AI to diagnose</span>
                </button>}
                <button className={`activity-action ${supportBundleCopied ? 'copied' : ''}`} type="button" onClick={() => void copySupportBundle(selectedActivity)}>
                  {supportBundleCopied ? <ClipboardCheck aria-hidden="true" /> : <Copy aria-hidden="true" />}
                  <span>{supportBundleCopied ? 'Bundle copied' : 'Copy support bundle'}</span>
                </button>
              </div>

              <dl className="activity-identifiers">
                <Identifier label="Started UTC" value={selectedActivity.startedAt} />
                <Identifier label="Duration" value={formatDuration(selectedActivity.durationMs)} />
                <Identifier label="Correlation ID" value={selectedActivity.correlationId} code />
                <Identifier label="Trace ID" value={selectedActivity.traceId ?? 'Unavailable'} code />
              </dl>

              <section className="activity-trace" aria-label="Distributed trace">
                <header>
                  <div>
                    <span><DatabaseZap aria-hidden="true" /> Evidence timeline</span>
                    <small>Portal, gateway, service, and MCP spans from Application Insights.</small>
                  </div>
                  <button type="button" disabled={traceLoading || !selectedActivity.traceId} onClick={() => void loadTrace(selectedActivity)}>
                    {traceLoading ? <Loader2 className="spinning" aria-hidden="true" /> : <RefreshCw aria-hidden="true" />}
                    <span>{trace ? 'Refresh trace' : 'Load trace'}</span>
                  </button>
                </header>

                {traceError && <p className="activity-trace-notice error"><TriangleAlert aria-hidden="true" />{traceError}</p>}
                {!trace && !traceLoading && !traceError && (
                  <div className="activity-trace-ready">
                    <span className="trace-line-preview"><i /><i /><i /><i /></span>
                    <strong>Trace ready on demand</strong>
                    <p>Load the exact distributed trace for this customer-owned activity. Only operational metadata is returned.</p>
                  </div>
                )}
                {traceLoading && (
                  <div className="activity-trace-loading" aria-live="polite">
                    <Loader2 className="spinning" aria-hidden="true" />
                    <span><strong>Resolving the evidence chain</strong><small>Querying the customer-owned trace window.</small></span>
                  </div>
                )}
                {trace?.status === 'pending' && (
                  <p className="activity-trace-notice"><CircleDot aria-hidden="true" />Telemetry is still ingesting. Refresh the trace in a few seconds.</p>
                )}
                {trace?.status === 'ready' && <TraceTimeline spans={trace.spans} />}
              </section>
            </>
          )}
        </section>
      </div>
    </section>
  );
}

function ActivitySignal({ value, label, tone }: { value: string; label: string; tone: 'mint' | 'blue' | 'danger' | 'muted' }): ReactNode {
  return <span className={`activity-signal ${tone}`}><strong>{value}</strong><small>{label}</small></span>;
}

function ActivityKindIcon({ kind }: { kind: PortalActivityKind }): ReactNode {
  if (kind === 'mcp') return <Server aria-hidden="true" />;
  if (kind === 'ai') return <Bot aria-hidden="true" />;
  if (kind === 'job') return <DatabaseZap aria-hidden="true" />;
  return <Activity aria-hidden="true" />;
}

function ActivityOutcome({ activity, prominent = false }: { activity: PortalActivityRecord; prominent?: boolean }): ReactNode {
  const succeeded = activity.outcome === 'succeeded';
  return (
    <span className={`activity-outcome ${activity.outcome} ${prominent ? 'prominent' : ''}`}>
      {succeeded ? <CheckCircle2 aria-hidden="true" /> : <XCircle aria-hidden="true" />}
      <span>{activity.statusCode ? `${activity.statusCode} ` : ''}{succeeded ? 'Succeeded' : 'Failed'}</span>
    </span>
  );
}

function Identifier({ label, value, code = false }: { label: string; value: string; code?: boolean }): ReactNode {
  return <div><dt>{label}</dt><dd className={code ? 'code' : undefined}>{value}</dd></div>;
}

function TraceTimeline({ spans }: { spans: PortalTraceSpan[] }): ReactNode {
  const maximumDuration = Math.max(1, ...spans.map((span) => span.durationMs ?? 0));
  return (
    <ol className="activity-trace-timeline">
      {spans.map((span) => (
        <li key={span.id} className={`${span.kind} ${span.success === false ? 'failed' : ''}`}>
          <span className="trace-node"><TraceSpanIcon span={span} /></span>
          <div className="trace-span-copy">
            <span><strong>{span.name}</strong><em>{formatTraceTime(span.timestamp)}</em></span>
            <div className="trace-duration-track" aria-hidden="true"><i style={{ width: `${Math.max(4, ((span.durationMs ?? 0) / maximumDuration) * 100)}%` }} /></div>
            <small>{traceSpanSummary(span)}</small>
          </div>
        </li>
      ))}
    </ol>
  );
}

function TraceSpanIcon({ span }: { span: PortalTraceSpan }): ReactNode {
  if (span.kind === 'exception' || span.success === false) return <XCircle aria-hidden="true" />;
  if (span.kind === 'dependency') return <DatabaseZap aria-hidden="true" />;
  if (span.kind === 'mcp') return <Server aria-hidden="true" />;
  return <CheckCircle2 aria-hidden="true" />;
}

function traceSpanSummary(span: PortalTraceSpan): string {
  const parts = [activityKindLabel(span.kind === 'request' ? 'rest' : span.kind === 'dependency' ? 'job' : span.kind === 'mcp' ? 'mcp' : 'ai')];
  if (span.durationMs !== undefined) parts.push(formatDuration(span.durationMs));
  if (span.resultCode) parts.push(`Result ${span.resultCode}`);
  return parts.join(' · ');
}

function formatActivityTitle(activity: PortalActivityRecord): string {
  return activity.title.replaceAll('_', ' ');
}

function activityKindLabel(kind: PortalActivityKind): string {
  if (kind === 'rest') return 'REST request';
  if (kind === 'mcp') return 'MCP tool';
  if (kind === 'ai') return 'AI analysis';
  return 'Operations job';
}

function replayLabel(activity: PortalActivityRecord): string {
  if (activity.kind === 'rest') return 'Replay in Workbench';
  if (activity.kind === 'mcp') return 'Open Agent Tool';
  if (activity.kind === 'job') return 'Open Jobs';
  return 'Open conversation';
}

function createReplayHref(activity: PortalActivityRecord): string {
  if (activity.kind === 'job') return '#jobs';
  if (activity.kind === 'mcp') return activity.toolName ? `#mcp-tools?tool=${encodeURIComponent(activity.toolName)}&tab=run` : '#mcp-tools';
  if (activity.kind === 'ai') return '#chat';
  const query = new URLSearchParams();
  if (activity.apiId) query.set('api', activity.apiId);
  if (activity.operationId) query.set('operation', activity.operationId);
  const queryString = query.toString();
  return queryString ? `#apis?${queryString}` : '#apis';
}

function createSupportBundle(activity: PortalActivityRecord, sessionStatus: SessionStatus): string {
  return [
    'iasset One integration support bundle',
    `Environment: ${sessionStatus.environment || 'Unavailable'}`,
    `Customer: ${sessionStatus.customerCode ?? 'Unavailable'}`,
    `Operation: ${formatActivityTitle(activity)}`,
    `Type: ${activity.kind}`,
    `Detail: ${activity.detail}`,
    `Started UTC: ${activity.startedAt}`,
    `Duration: ${activity.durationMs} ms`,
    `Outcome: ${activity.outcome}`,
    `HTTP status: ${activity.statusCode ?? 'Unavailable'}`,
    `Correlation ID: ${activity.correlationId}`,
    `Trace ID: ${activity.traceId ?? 'Unavailable'}`
  ].join('\n');
}

function createDiagnosisPrompt(activity: PortalActivityRecord, sessionStatus: SessionStatus): string {
  return [
    'Diagnose this portal integration activity using only the operational metadata below.',
    'Explain the most likely failure boundary or performance concern, state what the evidence does and does not prove, and give the next three checks.',
    `Environment: ${sessionStatus.environment || 'Unavailable'}`,
    `Customer: ${sessionStatus.customerCode ?? 'Unavailable'}`,
    `Operation: ${formatActivityTitle(activity)}`,
    `Type: ${activity.kind}`,
    `Detail: ${activity.detail}`,
    `Started UTC: ${activity.startedAt}`,
    `Duration: ${activity.durationMs} ms`,
    `Outcome: ${activity.outcome}`,
    `HTTP status: ${activity.statusCode ?? 'Unavailable'}`,
    `Correlation ID: ${activity.correlationId}`,
    `Trace ID: ${activity.traceId ?? 'Unavailable'}`
  ].join('\n');
}

function calculateMedian(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? Math.round((sorted[middle - 1] + sorted[middle]) / 2) : sorted[middle];
}

function calculatePercentile(values: number[], percentile: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((left, right) => left - right);
  return sorted[Math.min(sorted.length - 1, Math.ceil((percentile / 100) * sorted.length) - 1)];
}

function summarizeActivities(activities: PortalActivityRecord[]): PortalActivitySummary {
  const succeeded = activities.filter((activity) => activity.outcome === 'succeeded').length;
  const durations = activities.map((activity) => activity.durationMs);
  return {
    total: activities.length,
    succeeded,
    failed: activities.length - succeeded,
    medianDurationMs: calculateMedian(durations),
    p95DurationMs: calculatePercentile(durations, 95)
  };
}

function formatDateTimeLocal(timestamp: number): string {
  const date = new Date(timestamp);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

function formatAppliedRange(range: AppliedActivityRange): string {
  if (range.preset === 'live') return 'Live';
  if (range.preset === '1h') return 'Last hour';
  if (range.preset === '24h') return 'Last 24 hours';
  if (range.preset === '7d') return 'Last 7 days';
  if (range.preset === '30d') return 'Last 30 days';
  if (!range.from || !range.to) return 'Custom range';
  const formatter = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  return `${formatter.format(new Date(range.from))} – ${formatter.format(new Date(range.to))}`;
}

function formatDuration(milliseconds: number): string {
  if (milliseconds < 1000) return `${Math.round(milliseconds)} ms`;
  if (milliseconds < 60_000) return `${(milliseconds / 1000).toFixed(milliseconds < 10_000 ? 1 : 0)} s`;
  return `${Math.floor(milliseconds / 60_000)}m ${Math.round((milliseconds % 60_000) / 1000)}s`;
}

function formatRelativeTimestamp(timestamp: number): string {
  if (!Number.isFinite(timestamp)) return 'Unknown time';
  const seconds = Math.round((timestamp - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  if (Math.abs(seconds) < 60) return formatter.format(seconds, 'second');
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return formatter.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return formatter.format(hours, 'hour');
  return formatter.format(Math.round(hours / 24), 'day');
}

function formatTraceTime(value: string): string {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return 'Unknown time';
  return new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 }).format(timestamp);
}
